import { useCallback, useEffect, useState } from 'react';
import { type TextEditorPlugin } from '@meitu/saas-editor-core/es/plugins/text-editor';
import { LayerType, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { useEditor } from '@/hooks/use-editor';

/**
 * 获取文本图层当前有效的变形文字配置。
 *
 * 普通状态直接读取激活图层；进入文字编辑态后，工作图层会临时移除 `shapeExtension`，
 * 此时回退到编辑会话的原始图层，保证变形文字面板及列表、对齐、高亮等控件仍按变形文字状态展示。
 */
export const useEffectiveShapeExtension = (layerId: string) => {
    const editor = useEditor();
    const frame = editor.services.frame;
    const textEditor = editor.getInstance<TextEditorPlugin>('TextEditorPlugin');

    const resolve = useCallback(() => {
        const layer = frame.getActivePickLayer();
        const current =
            layer?.id === layerId && layer.layerType === LayerType.TEXT_LAYER
                ? ((layer.data as ITextLayerProps).shapeExtension ?? null)
                : null;

        if (current) return current;

        const session = textEditor?.getSession();
        if (!session || session.editingLayer.id !== layerId) return null;

        return (session.originLayer.data as ITextLayerProps).shapeExtension ?? null;
    }, [frame, layerId, textEditor]);

    const [shapeExtension, setShapeExtension] = useState(resolve);

    useEffect(() => {
        const update = () => setShapeExtension(resolve());
        update();

        const activeHandler = frame.onActiveLayerDataUpdate(update);
        const disposeSession = textEditor?.onSessionChange(update);

        return () => {
            editor.off(activeHandler);
            disposeSession?.();
        };
    }, [editor, frame, resolve, textEditor]);

    return shapeExtension;
};
